import { dia, shapes, util } from 'jointjs'
import invert from 'invert-color'
import { Artifact, ArtifactType, Link, LinkType } from '@/models'
import GraphState from './graph-state'

const defaultLinkLineStyle = {
  connection: true,
  stroke: 'black',
  strokeWidth: 2,
  targetMarker: {
    type: 'path',
    fill: 'black',
    stroke: 'none',
    d: 'm 12 -6 L -3 0 L 12 5 L 9 0 z',
  },
}

const dashedLinkLineStyle = {
  strokeDasharray: '8 4',
  strokeDashoffset: 8,
  targetMarker: {
    d: 'M 10 -5 L 0 0 L 10 5 L 6 0 z',
  },
}

export const CustomLink = dia.Link.define(
  'custom.Link',
  {
    attrs: {
      line: defaultLinkLineStyle,
      wrapper: {
        connection: true,
        strokeWidth: 10,
        strokeLinejoin: 'round',
      },
    },
    connector: {
      name: 'straight',
      args: { cornerType: 'cubic', precision: 0, cornerRadius: 20 },
    },
    z: 2,
    weight: 1,
    minLen: 1,
    labelPosition: 'c',
    labelOffset: 10,
    labelSize: {
      width: 50,
      height: 30,
    },
  },
  {
    markup: [
      {
        tagName: 'path',
        selector: 'wrapper',
        attributes: {
          fill: 'none',
          cursor: 'pointer',
          stroke: 'transparent',
        },
      },
      {
        tagName: 'path',
        selector: 'line',
        attributes: {
          fill: 'none',
          'pointer-events': 'none',
        },
      },
    ],
  },
)

function makeCell(opts: dia.Cell.Selectors): shapes.standard.Rectangle {
  const artifact = new shapes.standard.Rectangle()

  artifact.position(100, 30)
  artifact.resize(400, 40)
  artifact.attr('isCell', true)
  artifact.attr(opts)

  return artifact
}

function makeTypeContainer(artifactType: ArtifactType): shapes.standard.Rectangle {
  const bodyFillColor = colorFromApiColor(artifactType.color)

  const typeContainer = makeCell({
    body: {
      textAnchor: 'top',
      fill: bodyFillColor,
      fillOpacity: 1,
      rx: 10,
      ry: 10,
      strokeWidth: 0,
    },
    label: {
      textVerticalAnchor: 'top',
      refY: 15,
      fill: invert(bodyFillColor, true),
      fontSize: 16,
      fontWeight: 'bold',
      textWrap: {
        text: artifactType.name?.substring(0, 50),
        width: -20,
        ellipsis: true,
      },
    },
    foreignObject: {
      visibility: 'visible',
      width: 'calc(w)',
      height: 'calc(h)',
    },
  })

  typeContainer.prop(GraphState.PROP_ARTIFACT_TYPE, artifactType)
  typeContainer.resize(420, 60)
  typeContainer.prop('z', 1)

  typeContainer.prop('original_height', typeContainer.size().height)
  typeContainer.prop('original_width', typeContainer.size().width)
  typeContainer.attr('isCell', false)

  typeContainer.markup = util.svg/* xml */ `
      <rect @selector="body" />
      <text @selector="label" />
      <foreignObject @selector="search" overflow="hidden" visibility="hidden">
          <analyze-search-ce @selector="content" artifact-type-id="${artifactType.id}">
          </analyze-search-ce>
      </foreignObject>
  `

  return typeContainer
}

function makeArtifact(artifact: Artifact): shapes.standard.Rectangle {
  let artifactNameToDisplay: string
  let artifactNameShortened = false
  if (artifact.name.length > 50 && artifact.name.substring(40, 50).includes(' ')) {
    artifactNameToDisplay =
      artifact.name.substring(0, artifact.name.substring(0, 50).lastIndexOf(' ')) + '...'
    artifactNameShortened = true
  } else if (artifact.name.length > 50 && !artifact.name.substring(40, 50).includes(' ')) {
    artifactNameToDisplay = artifact.name.substring(0, 47) + '...'
    artifactNameShortened = true
  } else {
    artifactNameToDisplay = artifact.name
  }

  const bodyFillColor = lightenColor(colorFromApiColor(artifact.artifactType.color), 80)

  const artifactCell = makeCell({
    body: {
      fill: bodyFillColor,
      rx: 10,
      ry: 10,
      strokeWidth: 0,
    },
    label: {
      fill: invert(bodyFillColor, true),
      textWrap: {
        text: artifactNameToDisplay,
        width: -20,
        ellipsis: true,
      },
      class: 'pointer-cursor',
    },
    rect: {
      class: 'pointer-cursor',
    },
  })
  if (artifactNameShortened) {
    artifactCell.attr('label/data-tooltip', `${artifact.name}`)
    artifactCell.attr('label/data-tooltip-position', `left`)
  }

  // this is for placing the tooltip next to the artifact cell, but being triggered on the label.
  // it is not working reliantly, so it is commented for now.
  /* artifactCell.attr(
    'label/data-tooltip-position-selector',
    `[model-id='${artifactCell.id}'] [joint-selector='body']`,
  ) */

  artifactCell.prop(GraphState.PROP_ARTIFACT, artifact)
  artifactCell.prop('z', 2)
  artifactCell.set('locked', true)

  return artifactCell
}

function makeLink(
  model: Link | LinkType,
  source: dia.Cell,
  target: dia.Cell,
  opts?: { debug?: boolean; label?: string },
): shapes.standard.Link {
  const link = new CustomLink()

  link.source(source)
  link.target(target)

  if (Object.hasOwn(model, 'linkType')) {
    link.prop(GraphState.PROP_LINK, model)
  } else {
    link.prop(GraphState.PROP_LINK_TYPE, model)
  }

  // opts = { debug: true, ...opts }

  if (opts && opts.label) {
    link.appendLabel({ attrs: { text: { text: opts.label } } })
  }

  if (opts && opts.debug) {
    const sourceId =
      source.attributes.artifact !== undefined
        ? 'a ' + source.attributes.artifact.id
        : 'at ' + source.attributes.artifactType.id

    const targetId =
      target.attributes.artifact !== undefined
        ? 'a ' + target.attributes.artifact.id
        : 'at ' + target.attributes.artifactType.id

    const kindOfLink = Object.hasOwn(model, 'linkType') ? 'l' : 'lt'

    link.appendLabel({
      attrs: {
        text: { text: ` ${sourceId} -(${kindOfLink} ${model.id})-> ${targetId} ` },
      },
    })
  }
  if (
    target.attributes.artifactType?.artifactCount === 0 ||
    source.attributes.artifactType?.artifactCount === 0
  ) {
    link.attr({ line: { ...defaultLinkLineStyle, ...dashedLinkLineStyle } })
  }

  // link.router('manhattan', {})
  return link
}

function makeLoadMorePill(
  artifactType: ArtifactType,
  container: dia.Cell,
  totalNumberOfArtifacts: number,
  loadedArtifactsCount: number,
  searchMode: boolean,
): shapes.standard.Rectangle {
  const artifacttypeBodyFillColor = colorFromApiColor(artifactType.color)
  const fillColor = invert(artifacttypeBodyFillColor, true)

  const pill = makeCell({
    body: {
      rx: 15, // Set rx and ry to half of height to make it look like a pill
      ry: 15,
    },
    label: {
      text:
        loadedArtifactsCount.toString() + ' of ' + totalNumberOfArtifacts.toString() + ' loaded', // Initial label
      fill: fillColor,
      fontSize: 8,
      textAnchor: 'middle', // Center the label
      refX: '50%', // Center the label
      refY: '135%', // Center the label
      class: 'pointer-cursor',
    },
    foreignObject: {
      visibility: 'visible',
      width: 'calc(w)',
      height: 'calc(h)',
    },
  })

  pill.resize(100, 30) // Adjust the size as needed

  pill.set('locked', true)
  pill.set('isPill', true)
  if (searchMode) {
    pill.set('searchMode', true)
  }

  pill.prop('z', 3)
  pill.prop(GraphState.PROP_ARTIFACT_TYPE, artifactType)

  if (container.attributes.collapsed) {
    pill.set('hidden', true)
    pill.attr('./opacity', 0)
    pill.attr('./pointer-events', 'none')
  }

  pill.markup = util.svg/* xml */ `
      <text @selector="label" />
      <rect style="stroke-width: 0;" @selector="body" />
      <foreignObject @selector="load-more" overflow="hidden" visibility="hidden">
          <load-more-pill-ce pill-background-color="${fillColor}" @selector="content">
          </load-more-pill-ce>
      </foreignObject>
  `

  return pill
}

/**
 * The API returns colors as three decimal numbers between 0 and 255 for rgb values.
 * This function converts them to hex color strings.
 *
 * 0:0:0 => #000000
 * 255:255:255 => #ffffff
 */
export function colorFromApiColor(color: string): string {
  return (
    '#' +
    color
      .split(':')
      .map((s) => ('00' + parseInt(s).toString(16)).slice(-2))
      .join('')
  )
}

function lightenColor(color: string, amount: number): string {
  // Parse the color string to extract the RGB components
  const hexColor = color.replace('#', '')
  const r = parseInt(hexColor.substring(0, 2), 16)
  const g = parseInt(hexColor.substring(2, 4), 16)
  const b = parseInt(hexColor.substring(4, 6), 16)

  // Calculate the new RGB values by increasing the brightness
  const newR = Math.min(r + amount, 255)
  const newG = Math.min(g + amount, 255)
  const newB = Math.min(b + amount, 255)

  // Convert the new RGB values back to a hex color string
  const newHexColor = `#${newR.toString(16).padStart(2, '0')}${newG
    .toString(16)
    .padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`

  return newHexColor
}

function darkenFillColor(fillColor: string): string {
  if (!fillColor || !fillColor.startsWith('#')) return fillColor
  if (fillColor.length === 4) {
    fillColor =
      '#' + fillColor[1] + fillColor[1] + fillColor[2] + fillColor[2] + fillColor[3] + fillColor[3]
  }
  fillColor = fillColor.toLowerCase()

  const color = parseInt(fillColor.slice(1), 16)
  let r = (color >> 16) & 255
  let g = (color >> 8) & 255
  let b = color & 255

  r = Math.floor(r * 0.8)
  g = Math.floor(g * 0.8)
  b = Math.floor(b * 0.8)
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`
}

export { makeLink, makeArtifact, makeTypeContainer, makeLoadMorePill, darkenFillColor }
